import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./ViewAllLesson.module.css";
import { getAllLesson, getSportSchedule, updateLesson } from "../../WebServer/services/lesson/functionsLesson";
import useLiveRefresh from "../../locks/useLiveRefresh";
import { useNavigate } from "react-router-dom";
import Fabtn from "./../Global/Fabtn/Fabtn";
import { toast } from "../../ALERT/SystemToasts";
import { canStored } from "../../utils/session";
import { isLessonVisible } from "../../utils/lessonVisibility";
import { roomLabel } from "../../utils/rooms";
import { lessonDayOf } from "../../utils/lessonSchedule";
import { SYSTEM_TEACHER, lessonLabel, mergeSchedule } from "../../utils/sportSchedule";
import MobileLessonList from "./MobileLessonList";
import { ask } from "../Provides/confirmBus";
import FilterDialog from "../UI/FilterDialog.jsx";
import Button from "../UI/Button";

/* =======================
   Helpers
======================= */
const BASE_MIN = 8 * 60;   // 08:00
const END_MIN  = 23 * 60;  // 23:00
const dayNames = ["الأحد","الاثنين","الثلاثاء","الأربعاء","الخميس","الجمعة","السبت"]; // 1..7

const toHHMM = (min) => {
  const m = Math.max(0, Math.min(min ?? 0, 24 * 60));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

const minuteToPx = (min) =>
  Math.max(0, Math.min((min ?? BASE_MIN) - BASE_MIN, END_MIN - BASE_MIN));

const getStart = (l) => l?.date?.startMin ?? ((l?.date?.hh ?? 8) * 60);
const getEnd   = (l) => l?.date?.endMin   ?? (getStart(l) + 45);

/* =======================
   Update lesson (shared)
======================= */
async function saveReschedule({ lesson, newDay, newRoom, newStartMin, onReload }) {
  const dur = Math.max(1, getEnd(lesson) - getStart(lesson));
  const start = Math.max(BASE_MIN, Math.min(newStartMin, END_MIN - 1));
  const end = Math.min(start + dur, 24 * 60);

  const confirmed = await ask("change", {
    title: "نقل الدرس",
    message: `هل تريد نقل درس «${lesson.name}» إلى يوم ${dayNames[(newDay || 1) - 1]} الساعة ${toHHMM(start)}؟`,
  }).catch(() => false);
  if (!confirmed) return;

  try {
    // ملاحظة عربية
    const payload = {
      name: lesson.name,
      teacher: lesson.teacher,
      list_students: lesson.list_students,
      room: newRoom, // number
      date: { day: newDay, startMin: start, endMin: end }
    };

    const res = await updateLesson(lesson._id, payload);

    if (res?.ok || res?.status === 200) {
      toast.success("تم نقل الدرس بنجاح");
      onReload?.();
    } else {
      toast.error(res?.message || "تعذّر نقل الدرس");
    }
  } catch (e) {
    toast.error("تعذّر نقل الدرس");
  }
}

/* =======================
   WEEK VIEW (days columns)
   Columns: days (1..7)
======================= */
function WeekTimeline({
  lessons,
  canEdit,
  showMyLessons,
  navigate,
  onReload,
  setTooltip,
  teacherNames,
  onShowDetails,
}) {
  const byDay = useMemo(() => {
    const map = {1:[],2:[],3:[],4:[],5:[],6:[],7:[]};
    const uid = localStorage.getItem("user_id");
    const canViewAll = canStored("lessons.viewAll");

    for (const l of (lessons || [])) {
      if (!l?.date) continue;
      const day = Number(l.date.day);
      if (!(day >= 1 && day <= 7)) continue;

      if (isLessonVisible(l, { showMyLessons, canViewAll, uid })) {
        map[day].push(l);
      }
    }

    for (const d of Object.keys(map)) {
      map[d].sort((a,b) => getStart(a) - getStart(b));
    }
    return map;
  }, [lessons, showMyLessons]);

  const hourMarks = Array.from({ length: 16 }, (_, i) => 8 + i); // 08..23

  return (
    <div className={styles.timelineGrid}>
      <div className={styles.headerSpacer} />
      {dayNames.map((dn, i) => (
        <div key={i} className={styles.dayHeader}>{dn}</div>
      ))}

      {/* hours gutter */}
      <div className={styles.hoursGutter}>
        {hourMarks.map((h, idx) => (
          <div key={idx}>
            <div className={styles.hourLine} style={{ top: `${(20 + h * 60 - BASE_MIN)}px` }} />
            <div className={styles.hourLabel} style={{ top: `${(20 + h * 60 - BASE_MIN)}px` }}>
              {String(h).padStart(2, "0")}:00
            </div>
          </div>
        ))}
      </div>

      {/* day columns */}
      {dayNames.map((_dn, idx) => {
        const day = idx + 1; // 1..7

        return (
          <div
            key={day}
            className={styles.dayCol}
            onClick={(e) => {
              if (!canEdit) return;
              if (!canStored("lessons.edit")) return;

              const rect = e.currentTarget.getBoundingClientRect();
              const offsetY = e.clientY - rect.top;
              const clickedMin = BASE_MIN + Math.round(offsetY);

              navigate(`/lessons/new?day=${day}&startMin=${clickedMin}`);
            }}
            onDragOver={(e) => {
              if (canEdit && canStored("lessons.edit")) e.preventDefault();
            }}
            onDrop={(e) => {
              if (!canEdit) return;
              if (!canStored("lessons.edit")) return;

              const lessonId = e.dataTransfer.getData("lesson-id");
              if (!lessonId) return;

              const rect = e.currentTarget.getBoundingClientRect();
              const offsetY = e.clientY - rect.top;
              const targetStartMin = BASE_MIN + Math.round(offsetY);

              const all = Object.values(byDay).flat();
              const l = all.find(x => String(x._id) === String(lessonId));
              if (!l) return;

              saveReschedule({
                lesson: l,
                newDay: day,
                newRoom: Number(l?.room ?? 0),
                newStartMin: targetStartMin,
                onReload
              });
            }}
          >
            {(byDay[day] || []).map((l) => {
              const top = 30 + minuteToPx(getStart(l));
              const height = Math.max(10, getEnd(l) - getStart(l));
              return (
                <div
                  key={l._id}
                  className={styles.lessonBlock}
                  data-tour="lessons-open"
                  style={{ top, height }}
                  draggable={!l.readOnly && canEdit && canStored("lessons.edit")}
                  onClick={(ev) => {
                    if (l.readOnly) {
                      ev.stopPropagation();
                      onShowDetails?.(l);
                      return;
                    }
                    // Everyone who sees the lesson opens its page; editing is a button there.
                    ev.stopPropagation();
                    navigate(`/lessons/${l._id}`);
                  }}
                  onDragStart={(e) => e.dataTransfer.setData("lesson-id", l._id)}
                  onMouseEnter={(e) => {
                    setTooltip?.({
                      show: true,
                      x: e.clientX,
                      y: e.clientY,
                      content: (
                        <>
                          <div><b>الاسم:</b> {lessonLabel(l)}</div>
                          <div><b>المرشد:</b> {l.readOnly ? SYSTEM_TEACHER : (teacherNames?.[l.teacher] || "…")}</div>
                          <div><b>الوقت:</b> {toHHMM(getStart(l))}–{toHHMM(getEnd(l))}</div>
                          <div><b>اليوم:</b> {dayNames[(Number(l.date.day) || 1) - 1]}</div>
                          <div><b>المكان:</b> {roomLabel(l?.room)}</div>
                        </>
                      )
                    });
                  }}
                  onMouseMove={(e) => setTooltip?.({ x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setTooltip?.({ show: false })}
                >
                  <div className={styles.lessonTitle}>{lessonLabel(l)}</div>
                  <div className={styles.lessonMeta}>
                    {toHHMM(getStart(l))}–{toHHMM(getEnd(l))}
                    {" · "}
                    {roomLabel(l?.room)}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/* =======================
   DAY VIEW (rooms columns)
   Columns: rooms
======================= */
function DayRoomsTimeline({
  lessons,
  selectedDay,
  canEdit,
  showMyLessons,
  navigate,
  onReload,
  setTooltip,
  teacherNames,
  onShowDetails,
}) {
  const roomsList = useMemo(() => {
    const uid = localStorage.getItem("user_id");
    const canViewAll = canStored("lessons.viewAll");
    const set = new Set();

    for (const l of (lessons || [])) {
      if (!l?.date) continue;

      if (!isLessonVisible(l, { showMyLessons, canViewAll, uid })) continue;

      set.add(String(l?.room ?? 0));
    }

    if (set.size === 0) set.add("0");
    return Array.from(set).sort((a,b)=>Number(a)-Number(b));
  }, [lessons, showMyLessons]);

  const byRoom = useMemo(() => {
    const uid = localStorage.getItem("user_id");
    const canViewAll = canStored("lessons.viewAll");
    const map = Object.fromEntries(roomsList.map(r => [r, []]));

    for (const l of (lessons || [])) {
      if (!l?.date) continue;
      if (Number(l.date.day) !== Number(selectedDay)) continue;

      if (!isLessonVisible(l, { showMyLessons, canViewAll, uid })) continue;

      const rk = String(l?.room ?? 0);
      (map[rk] ??= []).push(l);
    }

    for (const k of Object.keys(map)) {
      map[k].sort((a,b) => getStart(a) - getStart(b));
    }
    return map;
  }, [lessons, roomsList, selectedDay, showMyLessons]);

  const hourMarks = Array.from({ length: 16 }, (_, i) => 8 + i);

  return (
    <div className={styles.timelineGridRooms} style={{ ["--cols"]: roomsList.length }}>
      <div className={styles.headerSpacer} />
      {roomsList.map((r) => (
        <div key={r} className={styles.dayHeader}>
          {roomLabel(r)}
        </div>
      ))}

      <div className={styles.hoursGutter}>
        {hourMarks.map((h, idx) => (
          <div key={idx}>
            <div className={styles.hourLine} style={{ top: `${(20 + h * 60 - BASE_MIN)}px` }} />
            <div className={styles.hourLabel} style={{ top: `${(20 + h * 60 - BASE_MIN)}px` }}>
              {String(h).padStart(2, "0")}:00
            </div>
          </div>
        ))}
      </div>

      {roomsList.map((room) => (
        <div
          key={room}
          className={styles.roomCol}
          onClick={(e) => {
            if (!canEdit) return;
            if (!canStored("lessons.edit")) return;

            const rect = e.currentTarget.getBoundingClientRect();
            const offsetY = e.clientY - rect.top;
            const clickedMin = BASE_MIN + Math.round(offsetY);

            navigate(`/lessons/new?day=${selectedDay}&room=${room}&startMin=${clickedMin}`);
          }}
          onDragOver={(e) => {
            if (canEdit && canStored("lessons.edit")) e.preventDefault();
          }}
          onDrop={(e) => {
            if (!canEdit) return;
            if (!canStored("lessons.edit")) return;

            const lessonId = e.dataTransfer.getData("lesson-id");
            if (!lessonId) return;

            const rect = e.currentTarget.getBoundingClientRect();
            const offsetY = e.clientY - rect.top;
            const targetStartMin = BASE_MIN + Math.round(offsetY);

            const l = (lessons || []).find(x => String(x._id) === String(lessonId));
            if (!l) return;

            saveReschedule({
              lesson: l,
              newDay: Number(selectedDay),
              newRoom: Number(room),
              newStartMin: targetStartMin,
              onReload
            });
          }}
        >
          {(byRoom[room] || []).map((l) => {
            const top = 30 + minuteToPx(getStart(l));
            const height = Math.max(10, getEnd(l) - getStart(l));

            return (
              <button
                key={l._id}
                className={styles.lessonBlock}
                data-tour="lessons-open"
                style={{ top, height }}
                draggable={!l.readOnly && canEdit && canStored("lessons.edit")}
                onClick={(ev) => {
                  if (l.readOnly) {
                    ev.stopPropagation();
                    onShowDetails?.(l);
                    return;
                  }
                  ev.stopPropagation();
                  navigate(`/lessons/${l._id}`);
                }}
                onDragStart={(e) => e.dataTransfer.setData("lesson-id", l._id)}
                onMouseEnter={(e) => {
                  setTooltip?.({
                    show: true,
                    x: e.clientX,
                    y: e.clientY,
                    content: (
                      <>
                        <div><b>الاسم:</b> {lessonLabel(l)}</div>
                        <div><b>المرشد:</b> {l.readOnly ? SYSTEM_TEACHER : (teacherNames?.[l.teacher] || "…")}</div>
                        <div><b>الوقت:</b> {toHHMM(getStart(l))}–{toHHMM(getEnd(l))}</div>
                        <div><b>اليوم:</b> {dayNames[(Number(l.date.day) || 1) - 1]}</div>
                        <div><b>المكان:</b> {roomLabel(room)}</div>
                      </>
                    )
                  });
                }}
                onMouseMove={(e) => setTooltip?.({ x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setTooltip?.({ show: false })}
              >
                <div className={styles.lessonTitle}>{lessonLabel(l)}</div>
                <div className={styles.lessonMeta}>
                  {toHHMM(getStart(l))}–{toHHMM(getEnd(l))}
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* =======================
   Read-only football lesson details
======================= */
function SportLessonDetails({ lesson, canOpenSports, onClose, onOpenSports }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const name = lessonLabel(lesson);
  return (
    <div className={styles.detailsBackdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.detailsBox} role="dialog" aria-modal="true" aria-label={name}>
        <h2 className={styles.detailsTitle}>{name}</h2>
        <p><b>اليوم والوقت:</b> {dayNames[(Number(lesson.date?.day) || 1) - 1]} {toHHMM(getStart(lesson))}–{toHHMM(getEnd(lesson))}</p>
        <p><b>المكان:</b> {roomLabel(lesson.room)}</p>
        <p><b>المرشد:</b> {SYSTEM_TEACHER}</p>
        <p><b>عدد اللاعبين:</b> {lesson.playersCount ?? 0}</p>
        <div className={styles.detailsActions}>
          {canOpenSports && (
            <button type="button" className={styles.detailsLink} onClick={onOpenSports}>إدارة من قسم الرياضة</button>
          )}
          <button type="button" className={styles.detailsClose} onClick={onClose}>إغلاق</button>
        </div>
      </div>
    </div>
  );
}

/* =======================
   MAIN PAGE
======================= */
export default function ViewAllLesson() {
  const navigate = useNavigate();
  const canCreateLessons = canStored("lessons.create");
  const canSeeSports = ["sports.view", "sports.coach", "sports.manage"].some((key) => canStored(key));

  const [showFab, setShowFab] = useState(false);
  const [addBtnEl, setAddBtnEl] = useState(null);
  const addBtnRef = useCallback((node) => {
    setAddBtnEl(node); // node = DOM element of the main button (or null)
  }, []);

  useEffect(() => {
    // if button not rendered (no admin) -> hide FAB
    if (!addBtnEl) {
      setShowFab(false);
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        // if main button is NOT in viewport -> show FAB
        setShowFab(!entry.isIntersecting);
      },
      { root: null, threshold: 0.01 }
    );

    io.observe(addBtnEl);
    return () => io.disconnect();
  }, [addBtnEl]);


  const [canEdit, setCanEdit] = useState(true);
  useEffect(() => { localStorage.setItem("canEdit", String(canEdit)); }, [canEdit]);

  const [isLoading, setIsLoading] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teacherNames, setTeacherNames] = useState({});
  const [details, setDetails] = useState(null); // a read-only sport lesson
  // Tooltips
  const [tooltipInfo, setTooltipInfo] = useState({ show:false, content:"", x:0, y:0 });

  // Filters
  const canViewAllLessons = canStored("lessons.viewAll");
  const [showMyLessons, setShowMyLessons] = useState(!canViewAllLessons);

  const [viewMode, setViewMode] = useState("week"); // "week" | "dayRooms"
  const [selectedDay, setSelectedDay] = useState(() => (new Date().getDay() + 1)); // 1..7

  const [filterDay, setFilterDay] = useState(0); // 0=all, 1..7 specific day (used mainly in week view)
  const [filterRoom, setFilterRoom] = useState("all"); // all | "0" | "1" | ...
  const [filterTeacher, setFilterTeacher] = useState("all"); // all | teacherId
  const [query, setQuery] = useState("");

  // quiet: a live refresh keeps the schedule on screen instead of showing the loading state.
  const loadData = async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setIsLoading(true);
      const [res, sportRes] = await Promise.all([getAllLesson(), getSportSchedule()]);
      if (!res?.ok) throw new Error(res?.message || "Load failed");

      // Football lessons are extra and read-only: the schedule still loads without them.
      const list = mergeSchedule(
        Array.isArray(res.lessons) ? res.lessons : [],
        sportRes?.ok ? sportRes.lessons : [],
      );
      // clean list_students
      const cleaned = list.map(lesson => ({
        ...lesson,
        list_students: Array.isArray(lesson.list_students)
          ? lesson.list_students.filter(x => x != null)
          : []
      }));

      setLessons(cleaned);
      // The staff names come with the lessons, so a guide (who may not read
      // the user list) sees them too; the filter offers only staff on lessons.
      const names = res.names || {};
      setTeacherNames(names);
      setTeachers(Object.entries(names)
        .map(([_id, name]) => ({ _id, name }))
        .sort((a, b) => a.name.localeCompare(b.name, "ar")));

    } catch {
      if (quiet) return;
      toast.error("تعذّر تحميل الدروس");
      setLessons([]);
      setTeacherNames({});
      setTeachers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);
  useLiveRefresh(["lesson", "sportLesson", "user"], () => { loadData({ quiet: true }); });

  // Options from lessons
  const roomOptions = useMemo(() => {
    const set = new Set((lessons || []).map(l => String(l?.room ?? 0)));
    if (set.size === 0) set.add("0");
    return Array.from(set).sort((a,b)=>Number(a)-Number(b));
  }, [lessons]);

  // Apply filters to a single list (always one calendar)
  const filteredLessons = useMemo(() => {
    const uid = localStorage.getItem("user_id");
    return (lessons || []).filter(l => {
      if (!l?.date) return false;

      // Filter day (global filter)
      if (filterDay !== 0 && Number(l.date.day) !== Number(filterDay)) return false;

      // Filter room
      const r = String(l?.room ?? 0);
      if (filterRoom !== "all" && r !== String(filterRoom)) return false;

      // Filter teacher
      if (filterTeacher !== "all" && String(l.teacher) !== String(filterTeacher)) return false;

      // Only my lessons (unless admin)
      if (!isLessonVisible(l, { showMyLessons, canViewAll: canViewAllLessons, uid })) return false;
      // search query by lesson name
      if (query.trim()) {
        const q = query.trim().toLowerCase();
        const name = String(l.name || "").toLowerCase();
        if (!name.includes(q)) return false;
      }

      return true;
    });
  }, [lessons, filterDay, filterRoom, filterTeacher, showMyLessons, query]);

  // Same breakpoint as .mobileView in ViewAllLesson.module.css.
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  useEffect(() => {
    const handleResize = () => {
      const m = window.innerWidth <= 900;
      setIsMobile(m);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // The day filter counts only where it shows (the week view on a desktop).
  const activeFilterCount =
    (filterRoom !== "all") + (filterTeacher !== "all") + (viewMode !== "dayRooms" && !isMobile && filterDay !== 0);
  const clearFilters = () => {
    setFilterRoom("all");
    setFilterTeacher("all");
    setFilterDay(0);
  };
  const [mobileDay, setMobileDay] = useState(() => lessonDayOf());

  const newLessonDay = isMobile
    ? mobileDay
    : viewMode === "dayRooms" ? selectedDay : (filterDay || 1);
  const addButton = canCreateLessons && (
    <Button
      ref={addBtnRef}
      id="page-add-lesson"
      data-tour="lessons-add"
      onClick={() => navigate(`/lessons/new?day=${newLessonDay}`)}
    >
      + إضافة درس جديد
    </Button>
  );

  return (
    <div>
      <h1 className={styles.title}>برنامج الدروس</h1>

      {/* ===== Filters + Controls ===== */}
      <div className={styles.controlsBar} data-tour="lessons-filters">
        <div className={styles.controlsCenter}>
          {!isMobile && <div className={styles.filterGroup}>
            <label>نوع الجدول</label>
            <select data-tour="lessons-view-mode" value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
              <option value="week">أسبوعي (حسب الأيام)</option>
              <option value="dayRooms">يومي (حسب الأماكن)</option>
            </select>
          </div>}

          {viewMode === "dayRooms" && (
            <div className={styles.filterGroup}>
              <label>اليوم</label>
              <select value={selectedDay} onChange={(e)=>setSelectedDay(Number(e.target.value))}>
                {dayNames.map((d, i)=>(
                  <option key={i+1} value={i+1}>{d}</option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.filterGroup}>
            <label htmlFor="lesson-search">بحث</label>
            <input
              id="lesson-search"
              data-tour="lessons-search"
              type="search"
              placeholder="اسم الدرس…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <FilterDialog count={activeFilterCount} onClear={clearFilters}>
            {viewMode !== "dayRooms" && !isMobile && <label>اليوم
              <select value={filterDay} onChange={(e)=>setFilterDay(Number(e.target.value))}>
                <option value={0}>الكل</option>
                {dayNames.map((d, i)=>(
                  <option key={i+1} value={i+1}>{d}</option>
                ))}
              </select>
            </label>}

            <label>المكان
              <select value={filterRoom} onChange={(e)=>setFilterRoom(e.target.value)}>
                <option value="all">الكل</option>
                {roomOptions.map(r=>(
                  <option key={r} value={r}>{roomLabel(r)}</option>
                ))}
              </select>
            </label>

            <label>المرشد
              <select value={filterTeacher} onChange={(e)=>setFilterTeacher(e.target.value)}>
                <option value="all">الكل</option>
                {(teachers || []).map(t => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
            </label>
          </FilterDialog>
          {!isMobile && addButton}
        </div>
        {isMobile && addButton}
      </div>

      {/* ===== Calendar (ONE) ===== */}
      {isLoading ? (
        <div className={styles.loaderWrap}>
          <div />
          {[...Array(7)].map((_, i) => <div key={i} className={styles.loaderBox} />)}
        </div>
      ) : (
        <>
          {/* Desktop */}
          <div className={styles.desktopView} data-tour={isMobile ? undefined : "lessons-calendar"}>
            {viewMode === "week" ? (
              <WeekTimeline
                lessons={filteredLessons}
                canEdit={canEdit}
                showMyLessons={showMyLessons}
                navigate={navigate}
                onReload={loadData}
                setTooltip={(t)=> setTooltipInfo(prev => ({ ...prev, ...t }))}
                teacherNames={teacherNames}
                onShowDetails={setDetails}
              />
            ) : (
              <DayRoomsTimeline
                lessons={filteredLessons}
                selectedDay={selectedDay}
                canEdit={canEdit}
                showMyLessons={showMyLessons}
                navigate={navigate}
                onReload={loadData}
                setTooltip={(t)=> setTooltipInfo(prev => ({ ...prev, ...t }))}
                teacherNames={teacherNames}
                onShowDetails={setDetails}
              />
            )}
          </div>

          <div className={styles.mobileView} data-tour={isMobile ? "lessons-calendar" : undefined}>
            <MobileLessonList
              lessons={filteredLessons}
              day={mobileDay}
              onDayChange={setMobileDay}
              teacherNames={teacherNames}
              onOpen={(l) => navigate(`/lessons/${l._id}`)}
              onShowDetails={setDetails}
            />
          </div>
        </>
      )}

      {details && (
        <SportLessonDetails
          lesson={details}
          canOpenSports={canSeeSports}
          onClose={() => setDetails(null)}
          onOpenSports={() => navigate("/sports")}
        />
      )}

      {/* Tooltip */}
      {tooltipInfo.show && (
        <div
          style={{
            position: "fixed",
            top: tooltipInfo.y + 12,
            left: tooltipInfo.x + 12,
            background: "black",
            color: "white",
            padding: 10,
            borderRadius: 6,
            pointerEvents: "none",
            zIndex: 9999,
            maxWidth: 300
          }}
        >
          {tooltipInfo.content}
        </div>
      )}

      <Fabtn
        anchor="#page-add-lesson"
        visible={showFab && canCreateLessons}
        label="إضافة درس"
        onClick={() => navigate(`/lessons/new?day=${newLessonDay}`)}
      />
    </div>
  );
}
